<template>
  <b-modal
    id="modal-edit-client-notification"
    ref="modal"
    v-model="open"
    size="ov-custom"
    no-close-on-backdrop
    hide-footer
    title="Edit Merchant Notification"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="clientNotificationForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form @submit.prevent="handleSubmit(handleClientNotificationSubmit)">
          <b-row>

            <!-- username -->
            <b-col cols="12">
              <b-form-group
                label="Add Your Message here"
                label-for="h-message"
              >
                <b-form-textarea
                  id="h-message"
                  v-model="form.notification_body"
                  :state="form.notification_body.length < notificationBodyMaxChar"
                  class="char-textarea"
                  :class="form.notification_body.length >= notificationBodyMaxChar ? 'text-danger' : ''"
                  :maxlength="notificationBodyMaxChar"
                  placeholder="Message"
                  rows="3"
                  required
                />
                <small
                  class="textarea-counter-value float-right"
                  :class="form.notification_body.length >= notificationBodyMaxChar ? 'bg-danger' : ''"
                >
                  <span class="char-count">{{ form.notification_body.length }}</span> / {{ notificationBodyMaxChar }}
                </small>
                <feather-icon
                  v-b-tooltip.hover.v-primary="'Click to view user guide'"
                  icon="AlertCircleIcon"
                  size="15"
                  class="cursor-pointer text-primary"
                />
                <small class="textarea-counter-massage-tooltip">
                  The maximum character count is 500.
                </small>
              </b-form-group>
            </b-col>

            <!-- email -->
            <b-col cols="12">
              <b-form-group
                label="Email Subject"
                label-for="d-subject"
              >
                <b-form-input
                  id="d-subject"
                  v-model="form.subject"
                  type="subject"
                  placeholder="Enter the subject to the email here"
                  autocomplete="nope"
                  :required="form.is_email_active"
                />
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                label="Merchants"
                label-for="merchant"
              >
                <v-select
                  v-model="form.merchant_ids"
                  :options="merchantOptions"
                  :reduce="option => option.id"
                  multiple
                  deselect-from-dropdown
                  label="name"
                  placeholder="Select Merchants"
                  @input="onMerchantChange"
                >
                  <template v-slot:option="option">
                    {{ option.name }}
                  </template>
                  <template #search="{attributes, events}">
                    <input
                      class="vs__search"
                      style="pointer-events:none"
                      :required="!form.merchant_ids || form.merchant_ids.length <= 0"
                      v-bind="attributes"
                      v-on="events"
                    >
                  </template>
                </v-select>
              </b-form-group>
            </b-col>

            <!-- checkbox -->
            <b-col cols="12">
              <b-form-group>

                <b-form-group
                  label="Broadcast Type"
                  label-for="d-broadcast-type"
                >

                  <div class="d-flex flex-wrap mt-1">
                    <b-form-checkbox
                      v-model="form.is_push_notification_active"
                      :value="true"
                      class="custom-control-secondary mr-2"
                    >
                      Send Portal Notification
                    </b-form-checkbox>
                    <b-form-checkbox
                      v-if="canSendSMS"
                      v-model="form.is_sms_active"
                      :value="true"
                      class="custom-control-secondary mr-2"
                    >
                      Send SMS
                    </b-form-checkbox>
                    <b-form-checkbox
                      v-if="canSendEmail"
                      v-model="form.is_email_active"
                      :value="true"
                      class="custom-control-secondary mr-2"
                    >
                      Send Email
                    </b-form-checkbox>
                    <b-form-checkbox
                      v-if="canSendWhatsApp"
                      v-model="form.is_whatsapp_active"
                      :value="true"
                      class="custom-control-secondary mr-2"
                    >
                      Send WhatsApp
                    </b-form-checkbox>
                  </div>
                </b-form-group>
              </b-form-group></b-col>

            <!-- submit and reset -->
            <b-col
              v-can="'create-merchant-notification'"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                class="mr-1 custom-button-color"
                :disabled="!hasFormChanged"
              >
                Submit
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                variant="outline-secondary"
                @click="clearForm"
              >
                Reset
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BRow, BFormCheckbox, BCol, BFormGroup, BFormInput, BButton, BOverlay, VBModal, BForm, BFormTextarea,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import vSelect from 'vue-select'
import _ from 'lodash'

const NotificationRepository = RepositoryFactory.get('notification')
const MerchantRepository = RepositoryFactory.get('merchant')

function initialState() {
  return {
    open: false,
    id: null,
    modalLoading: false,
    merchantOptions: [],
    notificationBodyMaxChar: 500,
    form: {
      is_sms_active: false,
      is_email_active: false,
      is_push_notification_active: false,
      is_whatsapp_active: false,
      notification_body: '',
      subject: '',
      merchant_ids: [],
    },
    originalForm: null,
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BOverlay,
    BFormCheckbox,
    BFormTextarea,
    vSelect,
  },
  mixins: [SuccessMessage, ErrorMessage],
  props: {
    emailConfig: { type: Object, required: true },
    smsConfig: { type: Object, required: true },
    whatsappConfig: { type: Object, required: true },
  },
  data() {
    return initialState()
  },
  computed: {
    canSendEmail() {
      const { emailConfig } = this
      return !!(
        emailConfig.host
      )
    },
    canSendSMS() {
      const { smsConfig } = this
      return !!(
        (smsConfig.url && smsConfig.url.trim()) || (smsConfig.auth_url && smsConfig.auth_url.trim())
      )
    },
    canSendWhatsApp() {
      const { whatsappConfig } = this
      return !!(
        whatsappConfig.token
      )
    },
    hasFormChanged() {
      // Use lodash's isEqual for deep comparison of objects and arrays
      return !_.isEqual(this.form, this.originalForm)
    },
  },
  methods: {
    async openModal(id) {
      Object.assign(this.$data, initialState())
      this.open = true
      this.modalLoading = true
      this.id = id
      await this.fetchClientNotificationDetails()
      await this.getMerchantList()
      this.originalForm = _.cloneDeep(this.form)
      this.modalLoading = false
    },
    async fetchClientNotificationDetails() {
      try {
        const { data } = await NotificationRepository.getMerchantNotificationById(this.id)
        const responseData = data.data

        // Since the backend now sends a real array or 'all',
        // we just need to convert 'all' to [-1] for the UI
        if (responseData.merchant_ids === 'all') {
          responseData.merchant_ids = [-1]
        } else if (Array.isArray(responseData.merchant_ids)) {
          responseData.merchant_ids = responseData.merchant_ids.map(Number)
        }

        this.form = responseData
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await MerchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
      this.merchantOptions.unshift({ id: -1, name: 'All' })
    },
    async handleClientNotificationSubmit() {
      try {
        this.modalLoading = true
        const payload = {
          is_sms_active: this.form.is_sms_active,
          is_email_active: this.form.is_email_active,
          is_whatsapp_active: this.form.is_whatsapp_active,
          is_push_notification_active: this.form.is_push_notification_active,
          notification_body: this.form.notification_body,
          subject: this.form.subject,
        }
        if (this.form.merchant_ids.includes(-1)) payload.merchant_ids = 'all'
        else payload.merchant_ids = this.form.merchant_ids

        const res = await NotificationRepository.updateMerchantNotification(this.id, payload)
        if (res.status === 200) {
          this.showSuccessMessage('Notification Updated Successfully')
          this.open = false
          this.$parent.fetchMerchantNotifications()
        } else {
          this.showErrorMessage('Notification Update Failed')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.modalLoading = false
      await this.window.reload()
    },
    clearForm() {
      this.form.is_sms_active = false
      this.form.is_email_active = false
      this.form.is_whatsapp_active = false
      this.form.is_push_notification_active = false
      this.form.notification_body = ''
      this.form.subject = ''
      this.form.merchant_ids = []
      this.form = _.cloneDeep(this.originalForm) || initialState().form
    },
    onMerchantChange() {
      if (this.form.merchant_ids.includes(-1)) this.form.merchant_ids = [-1]
    },
  },
}
</script>

<style>
.content-body-cell {
  /* Limit the text to 3 lines */
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* Add ellipsis for overflow */
  text-overflow: ellipsis;
  /* Adjust width as needed */
  max-width: 300px; /* Set the maximum width */
}

</style>
